import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { ResetFiltersButton } from "../../common/ResetFiltersButton.tsx";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import Badge from "@mui/material/Badge";
import Select from "@mui/material/Select";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarTodayIcon,
} from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { CARD_TITLE_STYLE } from "../../constants/cardStyles";
import { userTimezoneHeaders } from "../../utils/userTimezoneHeader";
import { readSrIdFromUnknown } from "../../utils/srIdDisplay.ts";
import { GLOBAL_TOOLTIP_PROPS } from "../../config/tooltips.config.ts";
import { ViewToggle } from "./ViewToggle.tsx";
import { ViewUseCaseDialog } from "./ViewUseCaseDialog.tsx";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog.tsx";
import {
  AddUseCaseDialog,
  AddUseCaseSavePayload,
} from "./AddUseCaseDialog.tsx";
import { EditUseCaseDialog } from "./EditUseCaseDialog.tsx";
import { formatDateDMY, formatAccuracy } from "../../utils/dateFormat.ts";
import {
  AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  AI_DETECTION_ROWS_PER_PAGE_OPTIONS,
} from "../../constants/aiDetectionPagination";
import toast from "react-hot-toast";
import React from "react";

/** Chevron + clear chip; matches Recycle Bin / Zone Management toolbar filters. */
const MUC_FILTER_SELECT_PR_CLEAR = "76px";
const MUC_FILTER_CLEAR_BTN_RIGHT_PX = 36;

const TOAST_POSITION = { position: "bottom-center" as const };

export interface UseCase {
  id: string;
  /** Backend `sr_id` — preferred label in ID column */
  srId?: string;
  name: string;
  modelFile: string;
  accuracy: number;
  description: string;
  uploadedOn: Date;
  isFavorite?: boolean;
  imagePreview?: string;
  useCaseType?: string;
  zoneType?: string;
}

/** API response shape for GET /use-cases/ (single item) */
interface UseCaseApiItem {
  id: string;
  name?: string;
  description?: string;
  use_case_type?: string;
  zone_type?: string;
  model_path?: string;
  model_size?: number;
  accuracy?: number;
  image_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  sr_id?: string | number;
}

/** Wrapped API response: { message, status, data } or similar */
interface UseCasesApiResponse {
  message?: string;
  status?: string;
  data?: UseCaseApiItem[];
  results?: UseCaseApiItem[];
}

const USE_CASES_API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

function mapApiItemToUseCase(item: UseCaseApiItem): UseCase {
  const base = USE_CASES_API_BASE.replace(/\/$/, "");
  const imageUrl = item.image_url?.trim();
  const imagePreview = imageUrl
    ? imageUrl.startsWith("http")
      ? imageUrl
      : `${base}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`
    : undefined;
  const createdAt = item.created_at?.trim();
  const uploadedOn = createdAt ? new Date(createdAt) : new Date();
  const uploadedOnDate = Number.isNaN(uploadedOn.getTime())
    ? new Date()
    : uploadedOn;
  return {
    id: String(item.id),
    srId: readSrIdFromUnknown(item),
    name: item.name ?? "",
    modelFile: item.model_path ?? "",
    accuracy: Number(item.accuracy ?? 0) || 0,
    description: item.description ?? "",
    uploadedOn: uploadedOnDate,
    isFavorite: false,
    imagePreview,
    useCaseType: item.use_case_type ?? "",
    zoneType: item.zone_type ?? "",
  };
}

/** Display only the last segment of model path (e.g. "model.pt") */
function getModelFileDisplayName(modelPath: string): string {
  if (!modelPath) return "";
  const segments = modelPath.replace(/\\/g, "/").split("/").filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : modelPath;
}

/** Extract use-case array from wrapped { message, status, data } or raw array response */
function extractUseCasesFromResponse(body: unknown): UseCaseApiItem[] {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const obj = body as Record<string, unknown>;
    const arr = (obj.data ?? obj.results ?? obj.Data) as unknown;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

// Sample data matching the pattern across tabs (fallback when API is unavailable)
const INITIAL_USE_CASES: UseCase[] = [
  
];

// Placeholder images for grid view - AI/ML related
const PLACEHOLDER_IMAGES = [
  // Technology
];

type ViewMode = "list" | "grid";
type Order = "asc" | "desc";
type OrderBy = keyof UseCase;

interface ManageUseCaseTableProps {
  title?: string;
  /** When set, shows error state with retry option (e.g. when backend fails). */
  loadError?: string | null;
  /** Called when user clicks Retry in error state. */
  onRetry?: () => void;
  /** When true, refetches use cases when tab becomes active. */
  isActive?: boolean;
}

export function ManageUseCaseTable({
  title = "Manage Use Case",
  loadError = null,
  onRetry,
  isActive = true,
}: ManageUseCaseTableProps) {
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [isLoadingUseCases, setIsLoadingUseCases] = useState(true);
  const [fetchUseCasesError, setFetchUseCasesError] = useState<string | null>(
    null,
  );

  const fetchUseCases = useCallback(async () => {
    if (!USE_CASES_API_BASE || !USE_CASES_API_BASE.trim()) {
      setFetchUseCasesError(
        "API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file to load use cases.",
      );
      setUseCases([]);
      setIsLoadingUseCases(false);
      return;
    }
    setIsLoadingUseCases(true);
    setFetchUseCasesError(null);
    try {
      const url = `${USE_CASES_API_BASE.replace(/\/$/, "")}/use-cases/`;
      const res = await fetch(url, {
        headers: { Accept: "application/json", ...userTimezoneHeaders() },
      });
      if (!res.ok) {
        throw new Error(res.statusText || `HTTP ${res.status}`);
      }
      const body: unknown = await res.json();
      const data = extractUseCasesFromResponse(body);
      const valid = data.filter(
        (item): item is UseCaseApiItem =>
          item != null && typeof item === "object" && "id" in item,
      );
      setUseCases(valid.map(mapApiItemToUseCase));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load use cases";
      setFetchUseCasesError(message);
      setUseCases([]);
    } finally {
      setIsLoadingUseCases(false);
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      fetchUseCases();
    }
  }, [isActive, fetchUseCases]);

  useEffect(() => {
    if (useCases.length === 0 && !isLoadingUseCases && !fetchUseCasesError)
      return;
    try {
      localStorage.setItem("manage-use-cases-data", JSON.stringify(useCases));
    } catch (error) {
      console.error("Error saving use cases to localStorage:", error);
    }
  }, [useCases, isLoadingUseCases, fetchUseCasesError]);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  );
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<OrderBy>("name");
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<{
    element: HTMLElement;
    useCase: UseCase;
    mouseX?: number;
    mouseY?: number;
  } | null>(null);

  const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("use-cases-show-favorites");
      return saved === "true";
    } catch {
      return false;
    }
  });

  // Filter states
  const [filterUseCaseType, setFilterUseCaseType] = useState<string>("all");
  const [filterZoneType, setFilterZoneType] = useState<string>("all");
  const [filterAccuracyRange, setFilterAccuracyRange] = useState<string>("all");
  const [filterDateRange, setFilterDateRange] = useState<string>("all");

  useEffect(() => {
    try {
      localStorage.setItem(
        "use-cases-show-favorites",
        String(showFavoritesOnly),
      );
    } catch (error) {
      console.error("Error saving favorites filter:", error);
    }
  }, [showFavoritesOnly]);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem("use-cases-view-mode");
      return (saved as ViewMode) || "list";
    } catch {
      return "list";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("use-cases-view-mode", viewMode);
    } catch (error) {
      console.error("Error saving view mode:", error);
    }
  }, [viewMode]);

  const handleViewModeChange = useCallback((newMode: ViewMode) => {
    setViewMode(newMode);
  }, []);

  // Dialog states
  const [newUseCaseDialogOpen, setNewUseCaseDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<UseCase | null>(null);
  const [selectedUseCase, setSelectedUseCase] = useState<UseCase | null>(null);
  const [selectedDisplayId, setSelectedDisplayId] = useState<string | null>(
    null,
  );
  const tableContentRef = useRef<HTMLDivElement>(null);
  const [tableContentHeight, setTableContentHeight] = useState(0);
  const [uploadStep, setUploadStep] = useState<"upload" | "form">("upload");
  useEffect(() => {
    const el = tableContentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setTableContentHeight(el.clientHeight);
    });
    ro.observe(el);
    setTableContentHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Form state for new/edit use case
  const [formData, setFormData] = useState({
    name: "",
    modelFile: "",
    accuracy: 0,
    description: "",
    imagePreview: "",
    useCaseType: "",
    zoneType: "",
  });

  const favoritesCount = useMemo(() => {
    return useCases.filter((uc) => uc.isFavorite).length;
  }, [useCases]);

  /** Unique AI use case names from loaded data (filter options match "AI Detection Use Case" column) */
  const useCaseNames = useMemo(() => {
    const names = Array.from(
      new Set(useCases.map((uc) => uc.name).filter(Boolean)),
    ) as string[];
    return names.sort((a, b) => a.localeCompare(b));
  }, [useCases]);

  // Reset use case filter if selected name no longer exists in data
  useEffect(() => {
    if (
      filterUseCaseType !== "all" &&
      useCaseNames.length > 0 &&
      !useCaseNames.includes(filterUseCaseType)
    ) {
      setFilterUseCaseType("all");
    }
  }, [filterUseCaseType, useCaseNames]);

  /** Client-side filtering: search and filters apply to full fetched dataset; no API call on search. */
  const filteredAndSortedUseCases = useMemo(() => {
    let filtered = [...useCases];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (uc) =>
          uc.name.toLowerCase().includes(q) ||
          uc.id.toLowerCase().includes(q) ||
          (uc.srId ?? "").toLowerCase().includes(q) ||
          uc.modelFile.toLowerCase().includes(q) ||
          uc.description.toLowerCase().includes(q),
      );
    }

    if (showFavoritesOnly) {
      filtered = filtered.filter((uc) => uc.isFavorite);
    }

    // Apply filters (filter by use case name so dropdown matches table column)
    if (filterUseCaseType !== "all") {
      filtered = filtered.filter((uc) => uc.name === filterUseCaseType);
    }
    if (filterZoneType !== "all") {
      filtered = filtered.filter((uc) => uc.zoneType === filterZoneType);
    }
    if (filterAccuracyRange !== "all") {
      const [min, max] = filterAccuracyRange.split("-").map(Number);
      filtered = filtered.filter(
        (uc) => uc.accuracy >= min && uc.accuracy <= max,
      );
    }
    if (filterDateRange !== "all") {
      let start: number;
      let end = Date.now();
      if (filterDateRange === "last7days") {
        start = new Date().setDate(new Date().getDate() - 7);
      } else if (filterDateRange === "last30days") {
        start = new Date().setDate(new Date().getDate() - 30);
      } else if (filterDateRange === "last3months") {
        start = new Date().setMonth(new Date().getMonth() - 3);
      } else {
        const parts = filterDateRange.split("-").map(Number);
        start = parts[0] ?? 0;
        end = parts[1] ?? end;
      }
      filtered = filtered.filter((uc) => {
        const date = (
          uc.uploadedOn instanceof Date
            ? uc.uploadedOn
            : new Date(uc.uploadedOn as string)
        ).getTime();
        return !Number.isNaN(date) && date >= start && date <= end;
      });
    }

    filtered.sort((a, b) => {
      let aValue: unknown = a[orderBy];
      let bValue: unknown = b[orderBy];

      if (orderBy === "uploadedOn") {
        aValue = (
          aValue instanceof Date ? aValue : new Date(aValue as string)
        ).getTime();
        bValue = (
          bValue instanceof Date ? bValue : new Date(bValue as string)
        ).getTime();
      }

      if (aValue == null || bValue == null) return 0;

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [
    useCases,
    searchQuery,
    showFavoritesOnly,
    order,
    orderBy,
    filterUseCaseType,
    filterZoneType,
    filterAccuracyRange,
    filterDateRange,
  ]);

  /** Pagination over filtered results (client-side); recalculated when filters/search change. */
  const paginatedUseCases = useMemo(() => {
    return filteredAndSortedUseCases.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredAndSortedUseCases, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [
    searchQuery,
    showFavoritesOnly,
    filterUseCaseType,
    filterZoneType,
    filterAccuracyRange,
    filterDateRange,
  ]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    showFavoritesOnly ||
    filterUseCaseType !== "all" ||
    filterDateRange !== "all";

  const handleResetFilters = () => {
    setSearchQuery("");
    setShowFavoritesOnly(false);
    setFilterUseCaseType("all");
    setFilterDateRange("all");
    setPage(0);
  };

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    useCase: UseCase,
  ) => {
    setAnchorEl({ element: event.currentTarget, useCase });
  };

  const handleContextMenu = (event: React.MouseEvent, useCase: UseCase) => {
    event.preventDefault();
    setAnchorEl({
      element: event.currentTarget as HTMLElement,
      useCase,
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleFavorite = (id: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setUseCases((prev) =>
      prev.map((uc) =>
        uc.id === id ? { ...uc, isFavorite: !uc.isFavorite } : uc,
      ),
    );
  };

  const handleDeleteClick = () => {
    if (anchorEl) {
      setItemToDelete(anchorEl.useCase);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = useCallback(() => {
    if (itemToDelete) {
      // Move to recycle bin (for now, just remove from list)
      setUseCases((prev) => prev.filter((item) => item.id !== itemToDelete.id));
      toast.success("Use case moved to Recycle Bin", TOAST_POSITION);
    }
    setItemToDelete(null);
  }, [itemToDelete]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNewUseCaseClick = () => {
    setUploadStep("upload");
    setUploadedFile(null);
    setFormData({
      name: "",
      modelFile: "",
      accuracy: 0,
      description: "",
      imagePreview: "",
      useCaseType: "",
      zoneType: "",
    });
    setNewUseCaseDialogOpen(true);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.name.endsWith(".zip")) {
      setUploadedFile(file);

      // Simulate metadata extraction from zip (in real scenario, this would parse the .json file)
      const mockMetadata = {
        name: "PPE Detection",
        modelFile: "ppe-detector.pt",
        accuracy: 92,
        description:
          "Helmet and vest detection for personal protective equipment compliance",
        imagePreview: "",
        useCaseType: "object_detection",
        zoneType: "polygon",
      };

      setFormData(mockMetadata);
      setUploadStep("form");
    }
  };

  const handleCreateUseCase = (data: AddUseCaseSavePayload) => {
    if (data.createdViaApi) {
      setNewUseCaseDialogOpen(false);
      fetchUseCases();
      toast.success("Use case created successfully", TOAST_POSITION);
      return;
    }
    const newUseCase: Omit<UseCase, "id"> = {
      name: data.name,
      modelFile: data.modelFile,
      accuracy: data.accuracy,
      description: data.description,
      uploadedOn: new Date(),
      isFavorite: false,
      imagePreview: data.imagePreview,
      useCaseType: data.useCaseType,
      zoneType: data.zoneType,
    };

    setUseCases((prev) => {
      const id = `UC-${String(prev.length + 1).padStart(3, "0")}`;
      return [...prev, { ...newUseCase, id }];
    });
    setPage(0);
    setNewUseCaseDialogOpen(false);
    toast.success("Use case created successfully", TOAST_POSITION);
  };

  const handleEditClick = (useCase: UseCase) => {
    setSelectedUseCase(useCase);
    setFormData({
      name: useCase.name,
      modelFile: useCase.modelFile,
      accuracy: useCase.accuracy,
      description: useCase.description,
      imagePreview: useCase.imagePreview || "",
      useCaseType: useCase.useCaseType || "",
      zoneType: useCase.zoneType || "",
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleUpdateUseCase = () => {
    if (!selectedUseCase) return;

    setUseCases((prev) =>
      prev.map((uc) =>
        uc.id === selectedUseCase.id
          ? { ...uc, name: formData.name, description: formData.description }
          : uc,
      ),
    );
    setEditDialogOpen(false);
  };

  const handleViewClick = (useCase: UseCase) => {
    setSelectedUseCase(useCase);
    const idx = paginatedUseCases.findIndex((u) => u.id === useCase.id);
    const displayId =
      useCase.srId?.trim() ||
      (/^UC-\d{3}$/.test(useCase.id ?? "")
        ? (useCase.id ?? "")
        : idx >= 0
          ? `UC-${String(page * rowsPerPage + idx + 1).padStart(3, "0")}`
          : (useCase.id ?? ""));
    setSelectedDisplayId(displayId);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // minHeight: "850px",
        height: "100%",
        bgcolor: "#fff",
      }}
    >
      {/* Header with Title and Info Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 400,
              color: "#FF8555",
            }}
          >
            {title}
          </Typography>
          <Tooltip
            title="Manage AI detection use cases and models for camera zones"
            {...GLOBAL_TOOLTIP_PROPS}
          >
            <IconButton
              size="small"
              sx={{
                color: "#9E9E9E",
                padding: "2px",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search, Filters, and Actions Row */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          px: { xs: 1, sm: 2 },
          pb: 2,
          alignItems: "center",
        }}
      >
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search use case..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: "240px" },
            minWidth: { xs: 0, sm: 240 },
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
            "& .MuiOutlinedInput-root": {
              height: 36,
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: "#fff",
              borderRadius: "10px",
              "& fieldset": {
                borderColor: "#E5E7EB",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Favorites Filter */}
        <Tooltip
          title={
            showFavoritesOnly ? "Show all use cases" : "Show favorites only"
          }
          {...GLOBAL_TOOLTIP_PROPS}
        >
          <IconButton
            size="small"
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              height: 36,
              width: 36,
              bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
              "&:hover": {
                bgcolor: showFavoritesOnly ? "#FFD0B8" : "#F5F5F5",
              },
            }}
          >
            <Badge
              badgeContent={favoritesCount}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "10px",
                  height: "16px",
                  minWidth: "16px",
                  bgcolor: "#2932E5",
                  borderRadius: "10px",
                },
              }}
            >
              {showFavoritesOnly ? (
                <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
              ) : (
                <StarBorderIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
              )}
            </Badge>
          </IconButton>
        </Tooltip>

        {/* View Toggle */}
        <ViewToggle
          view={viewMode}
          onChange={handleViewModeChange}
          ariaLabel="Toggle use cases view mode"
        />

        {/* Date Filter */}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: 100, sm: 120 }, position: "relative" }}
        >
          <Select
            value={filterDateRange}
            onChange={(e) => setFilterDateRange(e.target.value)}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: tableContentHeight
                    ? Math.round(tableContentHeight * 0.75)
                    : 400,
                },
              },
            }}
            sx={{
              height: 36,
              minWidth: { xs: 100, sm: 120 },
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#fff",
              borderRadius: "10px",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight:
                  filterDateRange !== "all"
                    ? `${MUC_FILTER_SELECT_PR_CLEAR} !important`
                    : undefined,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
              },
              "&:hover": {
                bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#f5f5f5",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2932E5",
              },
            }}
            startAdornment={
              <CalendarTodayIcon
                sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }}
              />
            }
          >
            <MenuItem value="all">All Dates</MenuItem>
            <MenuItem value="last7days">Last 7 Days</MenuItem>
            <MenuItem value="last30days">Last 30 Days</MenuItem>
            <MenuItem value="last3months">Last 3 Months</MenuItem>
          </Select>
          {filterDateRange !== "all" ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFilterDateRange("all");
              }}
              aria-label="Clear date filter"
              sx={{
                position: "absolute",
                right: `${MUC_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                p: 0.25,
                width: 28,
                height: 28,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </FormControl>

        {/* Use Case Type Filter */}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: 120, sm: 150 }, position: "relative" }}
        >
          <Select
            value={filterUseCaseType}
            onChange={(e) => setFilterUseCaseType(e.target.value)}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: tableContentHeight
                    ? Math.round(tableContentHeight * 0.5)
                    : 400,
                },
              },
            }}
            sx={{
              height: 36,
              minWidth: { xs: 120, sm: 150 },
              borderRadius: "10px",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: filterUseCaseType !== "all" ? "#FFE0D0" : "#fff",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight:
                  filterUseCaseType !== "all"
                    ? `${MUC_FILTER_SELECT_PR_CLEAR} !important`
                    : undefined,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
              },
              "&:hover": {
                bgcolor: filterUseCaseType !== "all" ? "#FFE0D0" : "#f5f5f5",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2932E5",
              },
            }}
            startAdornment={
              <FilterListIcon sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }} />
            }
          >
            <MenuItem value="all">All Use Cases</MenuItem>
            {useCaseNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
          {filterUseCaseType !== "all" ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFilterUseCaseType("all");
              }}
              aria-label="Clear use case type filter"
              sx={{
                position: "absolute",
                right: `${MUC_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                p: 0.25,
                width: 28,
                height: 28,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </FormControl>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* New Use Case Button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewUseCaseClick}
          sx={{
            bgcolor: "#2932E5",
            border: "0.8px solid #2932E5",
            borderRadius: "10px",
            color: "#fff",
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: 500,
            height: 36,
            px: { xs: 1.5, sm: 2 },
            boxShadow: "none",
            flexShrink: 0,
            "&:hover": {
              bgcolor: "#1f28b8",
              border: "0.8px solid #1f28b8",
              boxShadow: "none",
            },
          }}
        >
          New Use Case
        </Button>

        <ResetFiltersButton
          disabled={!hasActiveFilters}
          onClick={handleResetFilters}
        />
      </Box>

      {/* Error / Loading / Empty / Table or Grid View */}
      <Box
        ref={tableContentRef}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {loadError ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 48, color: "#B71C1C" }} />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                color: "#4A5565",
                textAlign: "center",
              }}
            >
              {loadError}
            </Typography>
            {onRetry && (
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
                sx={{
                  bgcolor: "#2932E5",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#1f28b8" },
                }}
              >
                Retry
              </Button>
            )}
          </Box>
        ) : fetchUseCasesError ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 48, color: "#B71C1C" }} />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                color: "#4A5565",
                textAlign: "center",
              }}
            >
              {fetchUseCasesError}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchUseCases}
              sx={{
                bgcolor: "#2932E5",
                textTransform: "none",
                "&:hover": { bgcolor: "#1f28b8" },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : isLoadingUseCases ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#6B7280",
              }}
            >
              Loading use cases...
            </Typography>
          </Box>
        ) : useCases.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              width={150}
              height={150}
              focusable="false"
              aria-hidden="true"
              sx={{ display: "block", color: "#D1D5DB" }}
            >
              <path
                fill="currentColor"
                d="M21.3353 12.0795L17.25 10.0357V5.25C17.2499 5.11075 17.2111 4.97428 17.1378 4.85586C17.0646 4.73743 16.9598 4.64174 16.8353 4.5795L12.3353 2.3295C12.2312 2.27748 12.1164 2.2504 12 2.2504C11.8836 2.2504 11.7689 2.27748 11.6648 2.3295L7.16475 4.5795C7.04019 4.64174 6.93542 4.73743 6.86216 4.85586C6.78891 4.97428 6.75007 5.11075 6.75 5.25V10.0365L2.66475 12.0787C2.54008 12.141 2.43523 12.2369 2.36197 12.3554C2.28871 12.474 2.24994 12.6106 2.25 12.75V18C2.24994 18.1394 2.28871 18.276 2.36197 18.3946C2.43523 18.5131 2.54008 18.609 2.66475 18.6713L7.16475 20.9213C7.26885 20.9733 7.38363 21.0003 7.5 21.0003C7.61637 21.0003 7.73115 20.9733 7.83525 20.9213L12 18.8385L16.1647 20.9213C16.2688 20.9733 16.3836 21.0003 16.5 21.0003C16.6164 21.0003 16.7312 20.9733 16.8353 20.9213L21.3353 18.6713C21.4599 18.609 21.5648 18.5131 21.638 18.3946C21.7113 18.276 21.7501 18.1394 21.75 18V12.75C21.7501 12.6106 21.7113 12.474 21.638 12.3554C21.5648 12.2369 21.4599 12.141 21.3353 12.0787M15.75 10.0357L12.75 11.5357V7.96275L15.75 6.46275V10.0357ZM12 3.83775L14.823 5.25L12 6.6615L9.177 5.25L12 3.83775ZM8.25 6.46275L11.25 7.96275V11.5357L8.25 10.0357V6.46275ZM6.75 19.0365L3.75 17.5365V13.9635L6.75 15.4635V19.0365ZM7.5 14.1615L4.677 12.75L7.5 11.3385L10.323 12.75L7.5 14.1615ZM8.25 15.4635L11.25 13.9635V17.5365L8.25 19.0365V15.4635ZM15.75 19.0365L12.75 17.5365V13.9635L15.75 15.4635V19.0365ZM16.5 14.1615L13.677 12.75L16.5 11.3385L19.323 12.75L16.5 14.1615ZM20.25 17.5365L17.25 19.0365V15.4635L20.25 13.9635V17.5365Z"
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                color: "#6B7280",
                textAlign: "center",
              }}
            >
              No use cases available.
            </Typography>
          </Box>
        ) : filteredAndSortedUseCases.length === 0 &&
          showFavoritesOnly &&
          favoritesCount === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <StarBorderIcon sx={{ fontSize: 40, color: "#D1D5DB" }} />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 500,
                color: "#4B5563",
                textAlign: "center",
              }}
            >
              No favorites yet
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#6B7280",
                textAlign: "center",
              }}
            >
              Click the star icon next to use cases to add them to favorites.
            </Typography>
          </Box>
        ) : filteredAndSortedUseCases.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              width={150}
              height={150}
              focusable="false"
              aria-hidden="true"
              style={{ display: "block", color: "#D1D5DB" }}
            >
              <path
                fill="currentColor"
                d="M21.3353 12.0795L17.25 10.0357V5.25C17.2499 5.11075 17.2111 4.97428 17.1378 4.85586C17.0646 4.73743 16.9598 4.64174 16.8353 4.5795L12.3353 2.3295C12.2312 2.27748 12.1164 2.2504 12 2.2504C11.8836 2.2504 11.7689 2.27748 11.6648 2.3295L7.16475 4.5795C7.04019 4.64174 6.93542 4.73743 6.86216 4.85586C6.78891 4.97428 6.75007 5.11075 6.75 5.25V10.0365L2.66475 12.0787C2.54008 12.141 2.43523 12.2369 2.36197 12.3554C2.28871 12.474 2.24994 12.6106 2.25 12.75V18C2.24994 18.1394 2.28871 18.276 2.36197 18.3946C2.43523 18.5131 2.54008 18.609 2.66475 18.6713L7.16475 20.9213C7.26885 20.9733 7.38363 21.0003 7.5 21.0003C7.61637 21.0003 7.73115 20.9733 7.83525 20.9213L12 18.8385L16.1647 20.9213C16.2688 20.9733 16.3836 21.0003 16.5 21.0003C16.6164 21.0003 16.7312 20.9733 16.8353 20.9213L21.3353 18.6713C21.4599 18.609 21.5648 18.5131 21.638 18.3946C21.7113 18.276 21.7501 18.1394 21.75 18V12.75C21.7501 12.6106 21.7113 12.474 21.638 12.3554C21.5648 12.2369 21.4599 12.141 21.3353 12.0787M15.75 10.0357L12.75 11.5357V7.96275L15.75 6.46275V10.0357ZM12 3.83775L14.823 5.25L12 6.6615L9.177 5.25L12 3.83775ZM8.25 6.46275L11.25 7.96275V11.5357L8.25 10.0357V6.46275ZM6.75 19.0365L3.75 17.5365V13.9635L6.75 15.4635V19.0365ZM7.5 14.1615L4.677 12.75L7.5 11.3385L10.323 12.75L7.5 14.1615ZM8.25 15.4635L11.25 13.9635V17.5365L8.25 19.0365V15.4635ZM15.75 19.0365L12.75 17.5365V13.9635L15.75 15.4635V19.0365ZM16.5 14.1615L13.677 12.75L16.5 11.3385L19.323 12.75L16.5 14.1615ZM20.25 17.5365L17.25 19.0365V15.4635L20.25 13.9635V17.5365Z"
              />
            </Box>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                color: "#6B7280",
                textAlign: "center",
              }}
            >
              No use cases match your filters.
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#9CA3AF",
                textAlign: "center",
              }}
            >
              Try adjusting your search or filters.
            </Typography>
          </Box>
        ) : viewMode === "list" ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <TableContainer
              component={Box}
              sx={{
                width: "100%",
                flex: 1,
                minHeight: { xs: 240, sm: 280 },
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: "fixed",
                  width: "100%",
                  minWidth: { xs: 720, sm: 880 },
                  bgcolor: "#fff",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    zIndex: 11,
                    backgroundColor: "#FAFAFA",
                    fontWeight: 500,
                    color: "#2932E5",
                    borderBottom: "1px solid #E5E7EB",
                  },
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    borderBottom: "1px solid #E5E7EB",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        bgcolor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#2932E5",
                        borderBottom: "1px solid #E5E7EB",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        width: 100,
                        minWidth: 100,
                      }}
                    >
                      ID
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#2932E5",
                        borderBottom: "1px solid #E5E7EB",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        cursor: "pointer",
                        width: 280,
                        minWidth: 200,
                      }}
                      onClick={() => handleSort("name")}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        AI Detection Use Case
                        {orderBy === "name" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#2932E5",
                        borderBottom: "1px solid #E5E7EB",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        cursor: "pointer",
                        width: 140,
                        minWidth: 120,
                      }}
                      onClick={() => handleSort("modelFile")}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Model File
                        {orderBy === "modelFile" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#2932E5",
                        borderBottom: "1px solid #E5E7EB",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        cursor: "pointer",
                        width: 200,
                        minWidth: 200,
                      }}
                      onClick={() => handleSort("accuracy")}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.1,
                          "& .MuiSvgIcon-root": { marginLeft: "2px" },
                        }}
                      >
                        Detection Accuracy
                        {orderBy === "accuracy" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#2932E5",
                        borderBottom: "1px solid #E5E7EB",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        width: 300,
                        minWidth: 300,
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      sx={{
                        bgcolor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#2932E5",
                        borderBottom: "1px solid #E5E7EB",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        cursor: "pointer",
                        width: 140,
                        minWidth: 140,
                      }}
                      onClick={() => handleSort("uploadedOn")}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        Uploaded On
                        {orderBy === "uploadedOn" &&
                          (order === "asc" ? (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          ))}
                      </Box>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        bgcolor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        borderBottom: "1px solid #E5E7EB",
                        px: "10px",
                        py: "8px",
                        verticalAlign: "middle",
                        width: 80,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: "#2932E5",
                          opacity:
                            hoveredRowId != null || anchorEl != null ? 1 : 0,
                          transition: "opacity 0.2s",
                        }}
                      >
                        Actions
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUseCases.map((useCase, index) => {
                    const displayId =
                      useCase.srId?.trim() ||
                      (/^UC-\d{3}$/.test(useCase.id ?? "")
                        ? (useCase.id ?? "")
                        : `UC-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);
                    return (
                      <TableRow
                        key={useCase.id}
                        onMouseEnter={() => setHoveredRowId(useCase.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        onContextMenu={(e) => handleContextMenu(e, useCase)}
                        sx={{
                          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#4A5565",
                            px: "10px",
                            py: "8px",
                            verticalAlign: "middle",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          {displayId}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#2932E5",
                            fontWeight: 600,
                            px: "10px",
                            py: "8px",
                            verticalAlign: "middle",
                            borderBottom: "1px solid #E5E7EB",
                            cursor: "pointer",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => handleViewClick(useCase)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {useCase.name}
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleToggleFavorite(useCase.id, e)
                              }
                              sx={{
                                padding: "4px",
                                opacity: hoveredRowId === useCase.id ? 1 : 0,
                                transition: "opacity 0.2s",
                              }}
                            >
                              {useCase.isFavorite ? (
                                <StarIcon
                                  sx={{ fontSize: 16, color: "#FFB400" }}
                                />
                              ) : (
                                <StarBorderIcon
                                  sx={{ fontSize: 16, color: "#9E9E9E" }}
                                />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#4A5565",
                            px: "10px",
                            py: "8px",
                            verticalAlign: "middle",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          {getModelFileDisplayName(useCase.modelFile)}
                        </TableCell>
                        <TableCell
                          sx={{
                            px: "10px",
                            py: "8px",
                            verticalAlign: "middle",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          <Chip
                            label={formatAccuracy(useCase.accuracy)}
                            size="small"
                            sx={{
                              bgcolor: "#E8F0FE",
                              color: "#1967D2",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "12px",
                              fontWeight: 500,
                              height: 24,
                              borderRadius: "4px",
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#4A5565",
                            px: "10px",
                            py: "8px",
                            verticalAlign: "middle",
                            borderBottom: "1px solid #E5E7EB",
                            maxWidth: 360,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {useCase.description}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            color: "#4A5565",
                            px: "10px",
                            py: "8px",
                            verticalAlign: "middle",
                            borderBottom: "1px solid #E5E7EB",
                          }}
                        >
                          {formatDateDMY(new Date(useCase.uploadedOn))}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            px: "10px",
                            py: "8px",
                            verticalAlign: "middle",
                            borderBottom: "1px solid #E5E7EB",
                            width: 80,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, useCase)}
                            sx={{
                              opacity: hoveredRowId === useCase.id ? 1 : 0,
                              transition: "opacity 0.2s",
                            }}
                          >
                            <MoreVertIcon
                              sx={{ fontSize: 20, color: "#4A5565" }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredAndSortedUseCases.length}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[...AI_DETECTION_ROWS_PER_PAGE_OPTIONS]}
              sx={{
                borderTop: "1px solid #E5E7EB",
                bgcolor: "#F2F2F2",
                flexShrink: 0,
                width: "100%",
                "& .MuiTablePagination-toolbar": {
                  minHeight: { xs: 44, sm: 52 },
                  px: { xs: 1, sm: 2 },
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                },
                "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                  {
                    fontFamily: "Roboto, sans-serif",
                    fontSize: { xs: "12px", sm: "14px" },
                    color: "#4A5565",
                    m: 0,
                  },
                "& .MuiTablePagination-actions": {
                  color: "#4A5565",
                },
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              width: "100%",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                p: { xs: 0.5, sm: 0 },
              }}
            >
              <Grid container spacing={1}>
                {paginatedUseCases.map((useCase, index) => {
                  const displayId =
                    useCase.srId?.trim() ||
                    (/^UC-\d{3}$/.test(useCase.id ?? "")
                      ? (useCase.id ?? "")
                      : `UC-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2}
                      key={useCase.id}
                      sx={{ display: "flex", minWidth: 0 }}
                    >
                      <Card
                        onMouseEnter={() => setHoveredRowId(useCase.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                        onClick={() => handleViewClick(useCase)}
                        onContextMenu={(e) => handleContextMenu(e, useCase)}
                        sx={{
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                          minWidth: 0,
                          height: { xs: "200px", sm: "220px" },
                          minHeight: { xs: 180, sm: 220 },
                          bgcolor: "rgb(255, 255, 255)",
                          color: "rgb(33, 33, 33)",
                          boxShadow: "var(--Paper-shadow)",
                          backgroundImage: "var(--Paper-overlay)",
                          overflow: "hidden",
                          borderRadius: "14px",
                          transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          border: "1px solid rgb(229, 231, 235)",
                          "&:hover": {
                            boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                            transform: "translateY(-4px) scale(1.02)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            minWidth: 0,
                            paddingLeft: { xs: 1, sm: "10px" },
                            paddingRight: { xs: 1, sm: "10px" },
                            pt: 1,
                            pb: 0,
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: "100%",
                              height: { xs: "96px", sm: "112.5px" },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "rgb(33, 33, 33)",
                              borderRadius: "8px",
                              overflow: "hidden",
                              position: "relative",
                              backgroundImage: useCase.imagePreview
                                ? `url(${useCase.imagePreview})`
                                : PLACEHOLDER_IMAGES.length > 0
                                  ? `url(${PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]})`
                                  : undefined,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, useCase);
                              }}
                              aria-label="Open actions menu"
                              sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                zIndex: 1,
                                color: "rgba(255,255,255,0.95)",
                                bgcolor: "rgba(255,255,255,0.15)",
                                padding: "6px",
                                "&:hover": {
                                  bgcolor: "rgba(255,255,255,0.3)",
                                  color: "#fff",
                                },
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleToggleFavorite(useCase.id, e)
                              }
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                color: useCase.isFavorite
                                  ? "#FFB400"
                                  : "rgba(255,255,255,0.95)",
                                bgcolor: "rgba(255,255,255,0.15)",
                                padding: "6px",
                                "&:hover": {
                                  bgcolor: "rgba(255,255,255,0.3)",
                                  color: useCase.isFavorite
                                    ? "#FFB400"
                                    : "#fff",
                                },
                              }}
                            >
                              {useCase.isFavorite ? (
                                <StarIcon sx={{ fontSize: 20 }} />
                              ) : (
                                <StarBorderIcon sx={{ fontSize: 20 }} />
                              )}
                            </IconButton>
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 10,
                                left: 10,
                                zIndex: 1,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: "20px",
                                bgcolor: "rgba(255, 179, 128, 0.92)",
                                color: "#fff",
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "11px",
                                fontWeight: 600,
                                letterSpacing: "0.02em",
                                textTransform: "uppercase",
                              }}
                            >
                              {(useCase.useCaseType || "USE CASE")
                                .replace(/_/g, " ")
                                .toUpperCase()}
                            </Box>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            p: { xs: 1, sm: 1.5 },
                            pt: { xs: 1, sm: 1.25 },
                            flex: 1,
                            minWidth: 0,
                            minHeight: { xs: 56, sm: 72 },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            overflow: "hidden",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "14px", sm: "16px" },
                              fontWeight: 700,
                              color: "#212121",
                              mb: 0.5,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            {useCase.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px" },
                              color: "#4A5565",
                              mb: 0.25,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            Accuracy: {formatAccuracy(useCase.accuracy)}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px" },
                              color: "#4A5565",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            ID: {displayId}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            {!loadError &&
              !fetchUseCasesError &&
              !isLoadingUseCases &&
              filteredAndSortedUseCases.length > 0 && (
                <TablePagination
                  component="div"
                  count={filteredAndSortedUseCases.length}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[...AI_DETECTION_ROWS_PER_PAGE_OPTIONS]}
                  sx={{
                    borderTop: "1px solid #E5E7EB",
                    bgcolor: "#F2F2F2",
                    flexShrink: 0,
                    width: "100%",
                    boxSizing: "border-box",
                    "& .MuiTablePagination-toolbar": {
                      minHeight: { xs: 44, sm: 52 },
                      px: { xs: 1, sm: 2 },
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    },
                    "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                      {
                        fontFamily: "Roboto, sans-serif",
                        fontSize: { xs: "12px", sm: "14px" },
                        color: "#4A5565",
                        m: 0,
                      },
                    "& .MuiTablePagination-actions": {
                      color: "#4A5565",
                    },
                  }}
                />
              )}
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl?.element}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorReference={anchorEl?.mouseX ? "anchorPosition" : "anchorEl"}
        anchorPosition={
          anchorEl?.mouseX && anchorEl?.mouseY
            ? { top: anchorEl.mouseY, left: anchorEl.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => anchorEl && handleViewClick(anchorEl.useCase)}>
          <VisibilityIcon sx={{ fontSize: 18, mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={() => anchorEl && handleEditClick(anchorEl.useCase)}>
          <EditIcon sx={{ fontSize: 18, mr: 1 }} />
          Edit
        </MenuItem>
      </Menu>

      {/* New Use Case Dialog */}
      <AddUseCaseDialog
        open={newUseCaseDialogOpen}
        onClose={() => setNewUseCaseDialogOpen(false)}
        onSave={handleCreateUseCase}
        existingModelNames={useCases.map((uc) => uc.modelFile)}
        existingUseCaseNames={useCases.map((uc) => uc.name)}
      />

      {/* Edit Dialog */}
      <EditUseCaseDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        useCase={selectedUseCase}
        onSave={(data) => {
          if (!selectedUseCase) return;
          setUseCases((prev) =>
            prev.map((uc) =>
              uc.id === selectedUseCase.id ? { ...uc, ...data } : uc,
            ),
          );
          setEditDialogOpen(false);
          toast.success("Changes saved successfully", TOAST_POSITION);
        }}
      />

      {/* View Dialog */}
      <ViewUseCaseDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        displayId={selectedDisplayId ?? undefined}
        useCase={selectedUseCase}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Use Case"
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action will move the entry to the Recycle Bin.`}
      />
    </Box>
  );
}
